import {
  AgentSessionManager,
  ApiUriConstants,
  HttpClient,
  HttpPollingRequestInit,
  HttpUtilService,
  Logger,
} from '@nice-ccf/core-sdk';
import { wrap } from 'comlink';
import { CXoneClient } from '@nice-ccf/agent-sdk';
import { SkillActivityEvent } from '../model/skill-activity-event';
import { SkillActivityWorker } from '../worker/skill-activity-worker';

/**
 * skill activity Provider Class
 */
export class CXoneSkillActivityProvider {
  private logger: Logger = new Logger('SDK', 'CXoneSkillActivityProvider');
  public pollingWorker: any;
  private agentSession = AgentSessionManager.instance;
  private baseUri = '';
  private utilService: HttpUtilService = new HttpUtilService();
  private cxoneClient = <CXoneClient>{};

  /**
   * Used to set the acd sdk base instance to access the subject from the base class
   * @example -
   */
  public setACDSdkBaseInstance(cxoneClient: CXoneClient) {
    this.cxoneClient = cxoneClient;
  }

  /**
   * Used to initiate the polling for skill activity data
   * @example -
   * ```
   * const skillActivityProvider = new CXoneSkillActivityProvider();
   * this.skillActivityProvider.startPolling();
   * ```
   */
  public startPolling() {
    this.logger.info('startPolling', 'startPolling in CXoneSkillActivityProvider');
    this.baseUri = this.agentSession.auth.getCXoneConfig().acdApiBaseUri;
    const authToken = this.agentSession.auth.getAuthToken().accessToken;
    if (this.baseUri && authToken) {
      const { startSkillActivityPolling } = this.initSkillActivityWorker();
      const Url = new URL(ApiUriConstants.SKILL_ACTIVITY_URI, this.baseUri);
      Url.searchParams.set(
        'fields',
        'agentsAvailable,agentsUnavailable,agentsLoggedIn,agentsWorking,campaignId,earliestQueueTime,emailFromAddress,inSLA,isActive,isNaturalCalling,isOutbound,mediaTypeId,mediaTypeName,outSLA,personalQueueCount,queueCount,serviceLevel,serviceLevelGoal,skillId,skillName,skillQueueCount'
      );
      Url.searchParams.set('updatedSince', new Date(0).toISOString());
      const reqInit: HttpPollingRequestInit = {
        headers: this.utilService.initPollingHeader(authToken, 'application/json'),
        method: 'GET',
      };
      HttpClient.getWithPolling(
        { url: Url.toString(), request: reqInit },
        this.handleSkillActivityResponse.bind(this),
        startSkillActivityPolling
      );
    }
  }

  /**
   * Used to terminate the polling of skill activity
   * @example -
   * ```
   * const agentActivityProvider = new CXoneSkillActivityProvider();
   * this.agentActivityProvider.terminatePolling();
   * ```
   */
  public terminatePolling() {
    this.terminateAgentWorker();
  }

  /**
   * Callback method which will passed on to the worker and will be executed after the polling api response
   * then will publish to the subject subscriber with activity data
   * @param response - activity api response object
   * @example -
   * ```
   * handleSkillActivityResponse(data);
   * ```
   */
  public handleSkillActivityResponse(response: any) {
    if (response) {
      const skillActivityEvent: Array<SkillActivityEvent> = this.formatSkillActivityResponse(response);
      if (skillActivityEvent.length) this.cxoneClient.skillActivityEvent.next(skillActivityEvent);
    } else {
      this.logger.error('handleSkillActivityResponse', 'Response is empty');
    }
  }
  /**
   * This method to format skill activity response
   * @param response -  activity api response object
   * @returns - array of skill activity's
   * @example -
   * ```
   * formatSkillActivityResponse(response);
   * ```
   */
  private formatSkillActivityResponse(response: any): Array<SkillActivityEvent> {  
    const agentActivityList = [] as Array<SkillActivityEvent>;
    if (response?.skillActivity && response.skillActivity.length > 0) {
      response.skillActivity.forEach((activity: { [key: string]: any }) => {
        const skillActivityEvent = new SkillActivityEvent();
        skillActivityEvent.parse(activity);
        agentActivityList.push(skillActivityEvent);
      });
    }
    return agentActivityList;
  }

  /**
   * Use to initializing the skill activity worker and will return the method inside the worker
   * @example
   * ```
   * this.initSkillActivityWorker();
   * ```
   */
  public initSkillActivityWorker() {
    if (!this.pollingWorker)
      this.pollingWorker = new Worker('../worker/skill-activity-worker', {
        name: 'ccf-skill-activity-polling-worker',
        type: 'module',
      });
    return wrap<SkillActivityWorker>(this.pollingWorker);
  }

  /**
   * Use to terminate the skill activity worker
   * @example -
   * ```
   * this.terminateAgentWorker
   * ```
   */
  private terminateAgentWorker() {
    this.pollingWorker.terminate();
    this.pollingWorker = undefined;
  }
}
